import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryState, isRedeliverable } from '@/services/db/delivery/utils';

/**
 * The expanded row's action-button visibility derives from the delivery's
 * resolved `deliveryState` (the DELIVERY_STATES priority winner), not from raw
 * timestamp presence: a message that was drafted *and* sent is `sent`, so it
 * offers Resend, not Send/Delete. This mirrors the model's state getters and is
 * distinct from the bulk-selection gate in `selection.ts` (which is
 * timestamp-presence based, matching `canBeSentOrDeleted`).
 */

/** A meta value present when it's a non-empty string. */
export function metaValue(delivery: Delivery, key: string): string | undefined {
  const value = delivery.meta?.[key];
  return value != null && value !== '' ? value : undefined;
}

export interface DeliveryRowState {
  isDrafted: boolean;
  isFailed: boolean;
  isUndeliverable: boolean;
  isAttempted: boolean;
  isRedeliverable: boolean;
  /** Attempted, then auto-retry-scheduled after hitting a message limit. */
  isRedeliverableAttempted: boolean;
}

export function deliveryRowState(delivery: Delivery): DeliveryRowState {
  const state = getDeliveryState(delivery);
  const isAttempted = state === 'attempted';
  return {
    isDrafted: state === 'drafted',
    isFailed: state === 'failed',
    isUndeliverable: state === 'undeliverable',
    isAttempted,
    isRedeliverable: isRedeliverable(delivery),
    isRedeliverableAttempted: isAttempted && metaValue(delivery, 'message_limit_retry') != null,
  };
}
